import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import DailyIframe from "@daily-co/daily-js";
import { httpsCallable } from "firebase/functions";
import {
  AlertTriangle,
  ArrowLeft,
  CalendarClock,
  Camera,
  CameraOff,
  Check,
  ClipboardCheck,
  Clock3,
  Expand,
  Loader2,
  LogIn,
  Mic,
  MicOff,
  MonitorUp,
  PhoneOff,
  RefreshCw,
  Send,
  ShieldCheck,
  Sparkles,
  Star,
  Users,
  Video,
  Wifi,
} from "lucide-react";
import "./VideoInterviewRoom.css";

const DEFAULT_FUNCTIONS = Object.freeze({
  getJoinToken: "getInterviewJoinToken",
  recordEvent: "markInterviewPresence",
  submitScorecard: "submitInterviewScorecard",
});

const STAFF_ROLES = new Set([
  "STAFF",
  "HR",
  "HR_MANAGER",
  "INTERVIEWER",
  "TECHNICAL",
  "MANAGER",
  "CEO",
  "ADMIN",
  "SUPER_ADMIN",
  "EXPERT",
  "EXTERNAL_PANEL",
]);

const RECOMMENDATIONS = [
  { value: "SELECTED", label: "Select candidate" },
  { value: "NEXT_ROUND", label: "Move to next round" },
  { value: "HOLD", label: "Keep on hold" },
  { value: "REJECTED", label: "Reject candidate" },
];

function timestampToMs(value) {
  if (!value) return 0;
  if (typeof value === "number") {
    return value < 10_000_000_000 ? value * 1000 : value;
  }
  if (typeof value === "string") return new Date(value).getTime();
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.seconds === "number") {
    return value.seconds * 1000 + Math.round((value.nanoseconds || 0) / 1_000_000);
  }
  return 0;
}

function formatDateTime(value) {
  const ms = timestampToMs(value);
  if (!ms) return "Schedule pending";
  return new Intl.DateTimeFormat("en-IN", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(ms));
}

function formatDuration(totalSeconds) {
  const seconds = Math.max(0, Math.floor(totalSeconds || 0));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = seconds % 60;
  if (hours > 0) {
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0"
    )}:${String(rest).padStart(2, "0")}`;
  }
  return `${String(minutes).padStart(2, "0")}:${String(rest).padStart(2, "0")}`;
}

function getDeviceLabel(device, index, fallback) {
  return device.label || `${fallback} ${index + 1}`;
}

function normaliseParticipants(rawParticipants = {}) {
  return Object.values(rawParticipants)
    .filter(Boolean)
    .map((participant) => ({
      id: participant.session_id,
      name:
        participant.user_name ||
        participant.user_id ||
        (participant.local ? "You" : "Interview participant"),
      local: Boolean(participant.local),
      audio: participant.audio !== false,
      video: participant.video !== false,
      joinedAt: Date.now(),
    }))
    .sort((a, b) => Number(b.local) - Number(a.local));
}

function getErrorMessage(error) {
  const code = String(error?.code || "");
  if (code.includes("permission-denied")) {
    return "This interview link is not authorised for your account.";
  }
  if (code.includes("unauthenticated")) {
    return "Please sign in with the same account that received this interview invite.";
  }
  if (code.includes("not-found")) {
    return "The interview invitation was not found or has been withdrawn.";
  }
  if (code.includes("failed-precondition")) {
    return error?.message || "The interview is not open yet.";
  }
  if (code.includes("deadline-exceeded")) {
    return "The secure interview link has expired.";
  }
  if (code.includes("resource-exhausted")) {
    return "Too many secure-room requests. Please wait one minute and try again.";
  }
  return (
    error?.message ||
    "We could not open the interview room. Check your connection and try again."
  );
}

function RatingField({ label, value, onChange }) {
  return (
    <div className="vir-rating-row">
      <span>{label}</span>
      <div className="vir-stars" role="radiogroup" aria-label={`${label} rating`}>
        {[1, 2, 3, 4, 5].map((score) => (
          <button
            type="button"
            key={score}
            className={score <= value ? "is-active" : ""}
            onClick={() => onChange(score)}
            aria-label={`${score} out of 5`}
            aria-checked={score === value}
            role="radio"
          >
            <Star size={20} fill={score <= value ? "currentColor" : "none"} />
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Secure browser-based interview room.
 *
 * Required props:
 * - firebaseFunctions: Firebase Functions instance created with getFunctions(app)
 * - interviewId: Firestore interview id
 * - inviteId: secure invite id/token reference
 * - viewerRole: "STAFF" or "CANDIDATE"
 * - viewerName: display name shown inside the room
 *
 * The getInterviewJoinToken Cloud Function must validate the signed-in user,
 * invite status and schedule before returning a short-lived Daily token.
 */
export default function VideoInterviewRoom({
  firebaseFunctions,
  interviewId: interviewIdProp,
  inviteId: inviteIdProp,
  viewerRole,
  viewerName = "",
  role = "CANDIDATE",
  currentUser = null,
  candidate = null,
  functionNames = DEFAULT_FUNCTIONS,
  companyName = "Shine Agri",
  onClose,
  onInterviewComplete,
}) {
  const queryParams = useMemo(
    () =>
      typeof window === "undefined"
        ? new URLSearchParams()
        : new URLSearchParams(window.location.search),
    []
  );
  const interviewIdFromInput =
    interviewIdProp ||
    queryParams.get("interviewId") ||
    queryParams.get("interview") ||
    "";
  const [interviewId, setInterviewId] = useState(interviewIdFromInput);
  const inviteId = inviteIdProp || queryParams.get("invite") || queryParams.get("inviteId") || "";
  const panelInviteId =
    queryParams.get("panelInvite") || queryParams.get("panelInviteId") || "";
  const queryRole = queryParams.get("viewerRole") || queryParams.get("role") || "";
  const normalisedRole = String(
    panelInviteId ? queryRole || "EXPERT" : viewerRole || queryRole || role || "CANDIDATE"
  ).toUpperCase();
  const isStaff = STAFF_ROLES.has(normalisedRole);
  const apiNames = useMemo(
    () => ({ ...DEFAULT_FUNCTIONS, ...(functionNames || {}) }),
    [functionNames]
  );

  const rootRef = useRef(null);
  const frameHostRef = useRef(null);
  const previewVideoRef = useRef(null);
  const previewStreamRef = useRef(null);
  const callFrameRef = useRef(null);
  const finishingRef = useRef(false);
  const waitingRecordedRef = useRef(false);
  const mountedRef = useRef(true);

  const [phase, setPhase] = useState("loading");
  const [session, setSession] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [error, setError] = useState("");
  const [statusText, setStatusText] = useState("Validating secure invitation…");
  const [devices, setDevices] = useState({ cameras: [], microphones: [] });
  const [selectedCamera, setSelectedCamera] = useState("");
  const [selectedMicrophone, setSelectedMicrophone] = useState("");
  const [cameraEnabled, setCameraEnabled] = useState(true);
  const [microphoneEnabled, setMicrophoneEnabled] = useState(true);
  const [previewActive, setPreviewActive] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [activeSpeakerId, setActiveSpeakerId] = useState("");
  const [networkState, setNetworkState] = useState("checking");
  const [isSharing, setIsSharing] = useState(false);
  const [eventNotice, setEventNotice] = useState("");
  const [scoreSubmitting, setScoreSubmitting] = useState(false);
  const [scoreSubmitted, setScoreSubmitted] = useState(false);
  const [scorecard, setScorecard] = useState({
    technical: 0,
    communication: 0,
    roleFit: 0,
    cultureFit: 0,
    overall: 0,
    recommendation: "",
    strengths: "",
    concerns: "",
    notes: "",
  });

  const scheduledAtMs = timestampToMs(
    session?.scheduledStartAt || session?.scheduledAt
  );
  const joinOpensAtMs =
    timestampToMs(session?.joinOpensAt) ||
    (scheduledAtMs ? scheduledAtMs - 15 * 60 * 1000 : 0);
  const expiresAtMs =
    timestampToMs(
      session?.tokenExpiresAt || session?.scheduledEndAt || session?.expiresAt
    ) ||
    (scheduledAtMs ? scheduledAtMs + 60 * 60 * 1000 : 0);
  const secondsUntilOpen = joinOpensAtMs
    ? Math.ceil((joinOpensAtMs - now) / 1000)
    : 0;
  const secondsRemaining = expiresAtMs
    ? Math.ceil((expiresAtMs - now) / 1000)
    : 60 * 60;
  const canJoinByTime =
    (!joinOpensAtMs || now >= joinOpensAtMs) && (!expiresAtMs || now < expiresAtMs);

  const callFunction = useCallback(
    async (name, payload) => {
      if (!firebaseFunctions) {
        throw new Error(
          "Firebase Functions is not connected. Pass firebaseFunctions={functions} to VideoInterviewRoom."
        );
      }
      const callable = httpsCallable(firebaseFunctions, name);
      const result = await callable(payload);
      return result.data || {};
    },
    [firebaseFunctions]
  );

  const recordEvent = useCallback(
    async (eventType, details = {}) => {
      if (!firebaseFunctions || !apiNames.recordEvent || !interviewId) return;
      const presenceEvent = {
        WAITING: "WAITING",
        INTERVIEW_JOINED: "JOINED",
        INTERVIEW_LEFT: "LEFT",
      }[eventType];
      if (!presenceEvent) return;
      try {
        await callFunction(apiNames.recordEvent, {
          interviewId,
          inviteId,
          inviteToken: inviteId,
          panelInviteId,
          panelInviteToken: inviteId,
          event: presenceEvent,
          role: normalisedRole,
          viewerRole: normalisedRole,
          clientAt: new Date().toISOString(),
          ...details,
        });
      } catch (recordError) {
        // Presence telemetry must never interrupt the live interview.
        console.warn("Interview event could not be recorded:", recordError);
      }
    },
    [
      apiNames.recordEvent,
      callFunction,
      firebaseFunctions,
      interviewId,
      inviteId,
      panelInviteId,
      normalisedRole,
    ]
  );

  const stopPreview = useCallback(() => {
    if (previewStreamRef.current) {
      previewStreamRef.current.getTracks().forEach((track) => track.stop());
      previewStreamRef.current = null;
    }
    if (previewVideoRef.current) previewVideoRef.current.srcObject = null;
    setPreviewActive(false);
  }, []);

  const refreshDevices = useCallback(async () => {
    if (
      typeof navigator === "undefined" ||
      !navigator.mediaDevices?.enumerateDevices
    ) {
      return;
    }
    const allDevices = await navigator.mediaDevices.enumerateDevices();
    const cameras = allDevices.filter((device) => device.kind === "videoinput");
    const microphones = allDevices.filter((device) => device.kind === "audioinput");
    if (!mountedRef.current) return;
    setDevices({ cameras, microphones });
    setSelectedCamera((value) => value || cameras[0]?.deviceId || "");
    setSelectedMicrophone((value) => value || microphones[0]?.deviceId || "");
  }, []);

  const startPreview = useCallback(async () => {
    setError("");
    if (
      typeof navigator === "undefined" ||
      !navigator.mediaDevices?.getUserMedia
    ) {
      setError("Camera and microphone are not supported in this browser.");
      return;
    }
    stopPreview();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: cameraEnabled
          ? selectedCamera
            ? { deviceId: { exact: selectedCamera } }
            : true
          : false,
        audio: microphoneEnabled
          ? selectedMicrophone
            ? { deviceId: { exact: selectedMicrophone } }
            : true
          : false,
      });
      if (!mountedRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      previewStreamRef.current = stream;
      if (previewVideoRef.current) {
        previewVideoRef.current.srcObject = stream;
        await previewVideoRef.current.play().catch(() => undefined);
      }
      setPreviewActive(true);
      await refreshDevices();
    } catch (mediaError) {
      setError(
        mediaError?.name === "NotAllowedError"
          ? "Camera or microphone permission was blocked. Allow access from the browser address bar and retry."
          : "The selected camera or microphone could not be opened."
      );
    }
  }, [
    cameraEnabled,
    microphoneEnabled,
    refreshDevices,
    selectedCamera,
    selectedMicrophone,
    stopPreview,
  ]);

  useEffect(() => {
    if (!previewActive || !previewVideoRef.current || !previewStreamRef.current) {
      return;
    }
    previewVideoRef.current.srcObject = previewStreamRef.current;
    previewVideoRef.current.play().catch(() => undefined);
  }, [phase, previewActive]);

  const destroyCallFrame = useCallback(async () => {
    const frame = callFrameRef.current;
    callFrameRef.current = null;
    if (!frame) return;
    try {
      await frame.destroy();
    } catch (destroyError) {
      console.warn("Daily room cleanup:", destroyError);
    }
    if (frameHostRef.current) frameHostRef.current.innerHTML = "";
  }, []);

  const finishMeeting = useCallback(
    async (reason = "left") => {
      if (finishingRef.current) return;
      finishingRef.current = true;
      setStatusText(reason === "time_expired" ? "Interview time completed" : "Interview ended");
      await recordEvent("INTERVIEW_LEFT", { reason });
      await destroyCallFrame();
      if (!mountedRef.current) return;
      setParticipants([]);
      setIsSharing(false);
      setPhase(
        isStaff && session?.canSubmitScorecard !== false ? "scorecard" : "complete"
      );
      onInterviewComplete?.({ interviewId, reason, role: normalisedRole });
    },
    [
      destroyCallFrame,
      interviewId,
      isStaff,
      normalisedRole,
      onInterviewComplete,
      recordEvent,
      session?.canSubmitScorecard,
    ]
  );

  const loadSecureSession = useCallback(async () => {
    setError("");
    setPhase("loading");
    setStatusText("Validating secure invitation…");
    finishingRef.current = false;
    if (!interviewId && !inviteId) {
      setError("Interview reference is missing from this link.");
      setPhase("error");
      return;
    }
    try {
      const response = await callFunction(apiNames.getJoinToken, {
        interviewId,
        inviteId,
        inviteToken: inviteId,
        panelInviteId,
        panelInviteToken: inviteId,
        role: normalisedRole,
        viewerRole: normalisedRole,
      });
      const returnedInterviewId =
        response.interviewId ||
        response.session?.interviewId ||
        response.interview?.id ||
        "";
      if (!interviewId && returnedInterviewId) {
        setInterviewId(returnedInterviewId);
      }
      const data = {
        ...response,
        ...(response.session || {}),
        roomUrl:
          response.roomUrl ||
          response.joinUrl ||
          response.url ||
          response.session?.roomUrl ||
          response.daily?.roomUrl ||
          "",
        token:
          response.token ||
          response.meetingToken ||
          response.dailyToken ||
          response.session?.token ||
          response.daily?.token ||
          "",
        scheduledStartAt:
          response.scheduledStartAt ||
          response.session?.scheduledStartAt ||
          response.scheduledAt ||
          response.session?.scheduledAt ||
          null,
        scheduledEndAt:
          response.scheduledEndAt ||
          response.session?.scheduledEndAt ||
          response.expiresAt ||
          response.session?.expiresAt ||
          null,
        roomStatus:
          response.roomStatus ||
          response.session?.roomStatus ||
          response.status ||
          "SCHEDULED",
      };
      if (!data.roomUrl || !data.token) {
        throw new Error("Secure meeting details were not returned by the server.");
      }
      if (["CANCELLED", "BLOCKED", "REJECTED"].includes(String(data.roomStatus).toUpperCase())) {
        throw new Error("This interview invitation is no longer active.");
      }
      if (!mountedRef.current) return;
      setSession(data);
      const openAt =
        timestampToMs(data.joinOpensAt) ||
        (timestampToMs(data.scheduledStartAt)
          ? timestampToMs(data.scheduledStartAt) - 10 * 60 * 1000
          : 0);
      const expiresAt =
        timestampToMs(data.tokenExpiresAt || data.scheduledEndAt) ||
        (timestampToMs(data.scheduledStartAt)
          ? timestampToMs(data.scheduledStartAt) + 60 * 60 * 1000
          : 0);
      if (expiresAt && Date.now() >= expiresAt) {
        setPhase("expired");
        setStatusText("This secure interview link has expired");
      } else if (openAt && Date.now() < openAt) {
        setPhase("waiting");
        setStatusText("Your secure interview room will open shortly");
      } else {
        setPhase("prejoin");
        setStatusText("Complete the device check before joining");
      }
      refreshDevices().catch(() => undefined);
    } catch (sessionError) {
      if (!mountedRef.current) return;
      setError(getErrorMessage(sessionError));
      setPhase("error");
    }
  }, [
    apiNames.getJoinToken,
    callFunction,
    interviewId,
    inviteId,
    panelInviteId,
    normalisedRole,
    refreshDevices,
  ]);

  useEffect(() => {
    mountedRef.current = true;
    loadSecureSession();
    const clock = window.setInterval(() => setNow(Date.now()), 1000);
    return () => {
      mountedRef.current = false;
      window.clearInterval(clock);
      stopPreview();
      const frame = callFrameRef.current;
      callFrameRef.current = null;
      if (frame) frame.destroy().catch(() => undefined);
    };
  }, [loadSecureSession, stopPreview]);

  useEffect(() => {
    if (phase === "waiting" && canJoinByTime) {
      setPhase("prejoin");
      setStatusText("Complete the device check before joining");
    }
    if (
      expiresAtMs &&
      now >= expiresAtMs &&
      ["waiting", "prejoin", "loading"].includes(phase)
    ) {
      stopPreview();
      setPhase("expired");
      setStatusText("This secure interview link has expired");
    }
  }, [canJoinByTime, expiresAtMs, now, phase, stopPreview]);

  useEffect(() => {
    if (
      isStaff ||
      waitingRecordedRef.current ||
      !interviewId ||
      phase !== "prejoin"
    ) {
      return;
    }
    waitingRecordedRef.current = true;
    recordEvent("WAITING");
  }, [interviewId, isStaff, phase, recordEvent]);

  const leaveMeeting = useCallback(
    async (reason = "left_by_user") => {
      const frame = callFrameRef.current;
      setPhase("leaving");
      try {
        if (frame) await frame.leave();
      } catch (leaveError) {
        console.warn("Daily leave:", leaveError);
      }
      await finishMeeting(reason);
    },
    [finishMeeting]
  );

  useEffect(() => {
    if (phase === "joined" && expiresAtMs && now >= expiresAtMs) {
      leaveMeeting("time_expired");
    }
  }, [expiresAtMs, leaveMeeting, now, phase]);

  const connectToMeeting = useCallback(async () => {
    if (!session || !canJoinByTime || callFrameRef.current || !frameHostRef.current) {
      return;
    }
    setError("");
    setStatusText("Opening encrypted interview room…");
    try {
      const frame = DailyIframe.createFrame(frameHostRef.current, {
        iframeStyle: {
          width: "100%",
          height: "100%",
          border: "0",
          borderRadius: "18px",
          backgroundColor: "#07111f",
        },
        showLeaveButton: false,
        showFullscreenButton: false,
        showParticipantsBar: false,
        showLocalVideo: true,
        activeSpeakerMode: true,
      });
      callFrameRef.current = frame;

      const syncParticipants = () => {
        if (!mountedRef.current || callFrameRef.current !== frame) return;
        setParticipants(normaliseParticipants(frame.participants()));
      };

      frame
        .on("joined-meeting", () => {
          if (!mountedRef.current) return;
          setPhase("joined");
          setStatusText("Live interview in progress");
          setNetworkState("connected");
          syncParticipants();
          recordEvent("INTERVIEW_JOINED", {
            participantCount: Object.keys(frame.participants() || {}).length,
          });
        })
        .on("left-meeting", () => finishMeeting("daily_left"))
        .on("participant-joined", (event) => {
          syncParticipants();
          setEventNotice(`${event?.participant?.user_name || "Participant"} joined`);
          window.setTimeout(() => mountedRef.current && setEventNotice(""), 3500);
          recordEvent("PARTICIPANT_JOINED", {
            participantSessionId: event?.participant?.session_id || "",
          });
        })
        .on("participant-updated", syncParticipants)
        .on("participant-left", (event) => {
          syncParticipants();
          setEventNotice(`${event?.participant?.user_name || "Participant"} left`);
          window.setTimeout(() => mountedRef.current && setEventNotice(""), 3500);
          recordEvent("PARTICIPANT_LEFT", {
            participantSessionId: event?.participant?.session_id || "",
          });
        })
        .on("active-speaker-change", (event) =>
          setActiveSpeakerId(event?.activeSpeaker?.peerId || "")
        )
        .on("network-connection", (event) =>
          setNetworkState(event?.event || event?.type || "connected")
        )
        .on("camera-error", (event) =>
          setError(event?.errorMsg || "Camera could not be started.")
        )
        .on("error", (event) => {
          setError(event?.errorMsg || "The video connection encountered an error.");
          setNetworkState("error");
        });

      await frame.join({
        url: session.roomUrl,
        token: session.token,
        userName:
          session.displayName ||
          viewerName ||
          currentUser?.displayName ||
          candidate?.name ||
          (isStaff ? "Interview panel" : "Candidate"),
      });

      if (selectedCamera || selectedMicrophone) {
        await frame
          .setInputDevicesAsync({
            videoDeviceId: selectedCamera || undefined,
            audioDeviceId: selectedMicrophone || undefined,
          })
          .catch(() => undefined);
      }
      frame.setLocalVideo(cameraEnabled);
      frame.setLocalAudio(microphoneEnabled);
    } catch (joinError) {
      await destroyCallFrame();
      if (!mountedRef.current) return;
      setError(getErrorMessage(joinError));
      setPhase("prejoin");
      setStatusText("Connection failed — verify devices and retry");
    }
  }, [
    cameraEnabled,
    canJoinByTime,
    candidate?.name,
    currentUser?.displayName,
    destroyCallFrame,
    finishMeeting,
    isStaff,
    microphoneEnabled,
    recordEvent,
    selectedCamera,
    selectedMicrophone,
    session,
    viewerName,
  ]);

  const joinMeeting = useCallback(() => {
    if (!session || !canJoinByTime || callFrameRef.current) return;
    setError("");
    setStatusText("Opening encrypted interview room…");
    stopPreview();
    setPhase("joining");
  }, [canJoinByTime, session, stopPreview]);

  useEffect(() => {
    if (phase !== "joining" || !frameHostRef.current || callFrameRef.current) return;
    connectToMeeting();
  }, [connectToMeeting, phase]);

  const toggleMicrophone = useCallback(() => {
    const next = !microphoneEnabled;
    setMicrophoneEnabled(next);
    callFrameRef.current?.setLocalAudio(next);
  }, [microphoneEnabled]);

  const toggleCamera = useCallback(() => {
    const next = !cameraEnabled;
    setCameraEnabled(next);
    callFrameRef.current?.setLocalVideo(next);
  }, [cameraEnabled]);

  const toggleShare = useCallback(async () => {
    const frame = callFrameRef.current;
    if (!frame) return;
    try {
      if (isSharing) {
        await frame.stopScreenShare();
        setIsSharing(false);
      } else {
        await frame.startScreenShare();
        setIsSharing(true);
      }
    } catch (shareError) {
      setError("Screen sharing was cancelled or is unavailable in this browser.");
    }
  }, [isSharing]);

  const requestFullscreen = useCallback(() => {
    if (!rootRef.current) return;
    if (document.fullscreenElement) {
      document.exitFullscreen?.();
    } else {
      rootRef.current.requestFullscreen?.();
    }
  }, []);

  const submitScorecard = useCallback(
    async (event) => {
      event.preventDefault();
      const requiredRatings = [
        scorecard.technical,
        scorecard.communication,
        scorecard.roleFit,
        scorecard.cultureFit,
        scorecard.overall,
      ];
      if (
        requiredRatings.some((rating) => !Number.isInteger(rating) || rating < 1) ||
        !scorecard.recommendation
      ) {
        setError("All five ratings and the final recommendation are required.");
        return;
      }
      setScoreSubmitting(true);
      setError("");
      try {
        await callFunction(apiNames.submitScorecard, {
          interviewId,
          inviteId,
          inviteToken: inviteId,
          panelInviteId,
          panelInviteToken: inviteId,
          role: normalisedRole,
          viewerRole: normalisedRole,
          scorecard,
        });
        if (!mountedRef.current) return;
        setScoreSubmitted(true);
        setPhase("complete");
        setStatusText("Interview evaluation submitted");
      } catch (submitError) {
        setError(getErrorMessage(submitError));
      } finally {
        if (mountedRef.current) setScoreSubmitting(false);
      }
    },
    [
      apiNames.submitScorecard,
      callFunction,
      interviewId,
      inviteId,
      panelInviteId,
      normalisedRole,
      scorecard,
    ]
  );

  const sessionTitle =
    session?.jobTitle ||
    session?.interviewTitle ||
    candidate?.jobTitle ||
    "Candidate interview";
  const candidateName =
    session?.candidateName ||
    candidate?.name ||
    (isStaff ? "Candidate" : viewerName || currentUser?.displayName);
  const panelLabel =
    session?.panelName ||
    session?.interviewerName ||
    (isStaff ? "Interview panel" : `${companyName} hiring team`);

  const renderHeader = () => (
    <header className="vir-header">
      <div className="vir-brand">
        <div className="vir-brand-mark">
          <Video size={20} />
        </div>
        <div>
          <strong>{companyName} Interview Studio</strong>
          <span>
            <ShieldCheck size={13} /> Secure hiring room
          </span>
        </div>
      </div>
      <div className="vir-session-heading">
        <strong>{sessionTitle}</strong>
        <span>{candidateName || panelLabel}</span>
      </div>
      <div className="vir-header-actions">
        <div
          className={`vir-live-pill ${phase === "joined" ? "is-live" : ""}`}
          aria-label="Meeting status"
        >
          <i />
          {phase === "joined" ? "LIVE" : "SECURE"}
        </div>
        <div className="vir-time-pill">
          <Clock3 size={15} />
          {phase === "joined" ? formatDuration(secondsRemaining) : "60 min"}
        </div>
        <button type="button" className="vir-icon-button" onClick={requestFullscreen}>
          <Expand size={18} />
          <span className="vir-sr-only">Toggle fullscreen</span>
        </button>
        {onClose && phase !== "joined" && phase !== "joining" && (
          <button type="button" className="vir-back-button" onClick={onClose}>
            <ArrowLeft size={16} /> Exit
          </button>
        )}
      </div>
    </header>
  );

  const renderError = () =>
    error ? (
      <div className="vir-alert" role="alert">
        <AlertTriangle size={17} />
        <span>{error}</span>
        <button type="button" onClick={() => setError("")} aria-label="Dismiss error">
          ×
        </button>
      </div>
    ) : null;

  const renderLoading = () => (
    <main className="vir-state-screen">
      <div className="vir-state-icon is-loading">
        <Loader2 size={34} className="vir-spin" />
      </div>
      <span className="vir-eyebrow">Identity & invitation check</span>
      <h1>Preparing your secure interview</h1>
      <p>{statusText}</p>
      <div className="vir-security-line">
        <ShieldCheck size={16} /> Time-limited access • authenticated participant • encrypted room
      </div>
    </main>
  );

  const renderWaiting = () => (
    <main className="vir-state-screen vir-waiting-screen">
      <div className="vir-state-icon">
        <CalendarClock size={34} />
      </div>
      <span className="vir-eyebrow">Interview scheduled</span>
      <h1>{sessionTitle}</h1>
      <p>
        The room opens {session?.joinOpenMinutes || 15} minutes before the
        interview. You can test your camera and microphone now.
      </p>
      <div className="vir-countdown">
        <span>Room opens in</span>
        <strong>{formatDuration(secondsUntilOpen)}</strong>
      </div>
      <div className="vir-schedule-card">
        <div>
          <CalendarClock size={19} />
          <span>
            <small>Date & time</small>
            <strong>
              {formatDateTime(session?.scheduledStartAt || session?.scheduledAt)}
            </strong>
          </span>
        </div>
        <div>
          <Users size={19} />
          <span>
            <small>Interview panel</small>
            <strong>{panelLabel}</strong>
          </span>
        </div>
        <div>
          <ShieldCheck size={19} />
          <span>
            <small>Secure duration</small>
            <strong>60 minutes</strong>
          </span>
        </div>
      </div>
      <button type="button" className="vir-secondary-button" onClick={startPreview}>
        <Camera size={17} /> Test camera & microphone
      </button>
      {previewActive && (
        <div className="vir-inline-preview">
          <video ref={previewVideoRef} muted playsInline autoPlay />
          <span>
            <Check size={14} /> Devices ready
          </span>
        </div>
      )}
    </main>
  );

  const renderPrejoin = () => (
    <main className="vir-prejoin">
      <section className="vir-preview-panel">
        <div className="vir-preview-video">
          <video ref={previewVideoRef} muted playsInline autoPlay />
          {!previewActive && (
            <div className="vir-preview-placeholder">
              {cameraEnabled ? <Camera size={38} /> : <CameraOff size={38} />}
              <strong>{cameraEnabled ? "Start device preview" : "Camera is off"}</strong>
              <span>Check how you look and sound before joining.</span>
            </div>
          )}
          <div className="vir-preview-name">
            {viewerName ||
              currentUser?.displayName ||
              candidateName ||
              (isStaff ? "Interviewer" : "Candidate")}
          </div>
          {previewActive && <div className="vir-preview-ready">Device preview</div>}
        </div>
        <div className="vir-preview-controls">
          <button
            type="button"
            className={!microphoneEnabled ? "is-off" : ""}
            onClick={() => setMicrophoneEnabled((value) => !value)}
          >
            {microphoneEnabled ? <Mic size={18} /> : <MicOff size={18} />}
            {microphoneEnabled ? "Mic on" : "Mic off"}
          </button>
          <button
            type="button"
            className={!cameraEnabled ? "is-off" : ""}
            onClick={() => setCameraEnabled((value) => !value)}
          >
            {cameraEnabled ? <Camera size={18} /> : <CameraOff size={18} />}
            {cameraEnabled ? "Camera on" : "Camera off"}
          </button>
          <button type="button" onClick={startPreview}>
            <RefreshCw size={18} /> {previewActive ? "Restart test" : "Test devices"}
          </button>
        </div>
      </section>

      <section className="vir-join-panel">
        <span className="vir-eyebrow">Device check</span>
        <h1>Ready to join?</h1>
        <p>
          Your interview room is open. Confirm the correct camera and microphone, then
          join securely.
        </p>

        <label>
          <span>Camera</span>
          <select
            value={selectedCamera}
            onChange={(event) => setSelectedCamera(event.target.value)}
            disabled={!cameraEnabled}
          >
            {devices.cameras.length === 0 && <option value="">Default camera</option>}
            {devices.cameras.map((device, index) => (
              <option key={device.deviceId} value={device.deviceId}>
                {getDeviceLabel(device, index, "Camera")}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span>Microphone</span>
          <select
            value={selectedMicrophone}
            onChange={(event) => setSelectedMicrophone(event.target.value)}
            disabled={!microphoneEnabled}
          >
            {devices.microphones.length === 0 && (
              <option value="">Default microphone</option>
            )}
            {devices.microphones.map((device, index) => (
              <option key={device.deviceId} value={device.deviceId}>
                {getDeviceLabel(device, index, "Microphone")}
              </option>
            ))}
          </select>
        </label>

        <div className="vir-join-summary">
          <span>
            <CalendarClock size={17} />{" "}
            {formatDateTime(session?.scheduledStartAt || session?.scheduledAt)}
          </span>
          <span>
            <Clock3 size={17} /> Link expires in {formatDuration(secondsRemaining)}
          </span>
          <span>
            <ShieldCheck size={17} /> Invite verified
          </span>
        </div>

        <button type="button" className="vir-primary-button" onClick={joinMeeting}>
          <LogIn size={18} /> Join secure interview
        </button>
        <small className="vir-consent">
          By joining, you agree to use your camera and microphone for this interview.
          Recording is enabled only when consent is separately displayed.
        </small>
      </section>
    </main>
  );

  const renderLiveMeeting = () => (
    <main className="vir-live-layout">
      <section className="vir-room-shell">
        <div className="vir-room-status">
          <span>
            <Wifi size={15} />{" "}
            {networkState === "connected" ? "Connection stable" : networkState}
          </span>
          <span>{participants.length} participant(s)</span>
        </div>
        <div className="vir-frame-host" ref={frameHostRef} />
        {phase === "joining" && (
          <div className="vir-joining-overlay">
            <Loader2 size={34} className="vir-spin" />
            <strong>Opening interview room</strong>
            <span>Please keep this window open.</span>
          </div>
        )}
        {eventNotice && <div className="vir-event-toast">{eventNotice}</div>}
        <div className="vir-call-controls">
          <button
            type="button"
            className={!microphoneEnabled ? "is-off" : ""}
            onClick={toggleMicrophone}
          >
            {microphoneEnabled ? <Mic size={21} /> : <MicOff size={21} />}
            <span>{microphoneEnabled ? "Mute" : "Unmute"}</span>
          </button>
          <button
            type="button"
            className={!cameraEnabled ? "is-off" : ""}
            onClick={toggleCamera}
          >
            {cameraEnabled ? <Camera size={21} /> : <CameraOff size={21} />}
            <span>{cameraEnabled ? "Camera" : "Start video"}</span>
          </button>
          {isStaff && (
            <button
              type="button"
              className={isSharing ? "is-active" : ""}
              onClick={toggleShare}
            >
              <MonitorUp size={21} />
              <span>{isSharing ? "Stop share" : "Share"}</span>
            </button>
          )}
          <button type="button" onClick={requestFullscreen}>
            <Expand size={21} />
            <span>Full screen</span>
          </button>
          <button
            type="button"
            className="vir-end-call"
            onClick={() => leaveMeeting("left_by_user")}
          >
            <PhoneOff size={22} />
            <span>Leave</span>
          </button>
        </div>
      </section>

      <aside className="vir-live-sidebar">
        <div className="vir-sidebar-head">
          <div>
            <span className="vir-eyebrow">Live room</span>
            <h2>Participants</h2>
          </div>
          <span className="vir-participant-count">{participants.length}</span>
        </div>
        <div className="vir-participant-list">
          {participants.length === 0 ? (
            <div className="vir-empty-participant">
              <Users size={24} />
              <span>Waiting for participants…</span>
            </div>
          ) : (
            participants.map((participant) => (
              <div
                className={`vir-participant ${
                  activeSpeakerId === participant.id ? "is-speaking" : ""
                }`}
                key={participant.id}
              >
                <div>{participant.name.slice(0, 1).toUpperCase()}</div>
                <span>
                  <strong>
                    {participant.name}
                    {participant.local ? " (You)" : ""}
                  </strong>
                  <small>
                    {activeSpeakerId === participant.id ? "Speaking" : "Connected"}
                  </small>
                </span>
                {participant.audio ? <Mic size={14} /> : <MicOff size={14} />}
              </div>
            ))
          )}
        </div>
        <div className="vir-session-panel">
          <span>
            <Clock3 size={17} />
            <small>Secure time remaining</small>
          </span>
          <strong>{formatDuration(secondsRemaining)}</strong>
          <div>
            <i
              style={{
                width: `${Math.max(
                  0,
                  Math.min(100, (secondsRemaining / (60 * 60)) * 100)
                )}%`,
              }}
            />
          </div>
          <p>The room closes automatically when the secure token expires.</p>
        </div>
        <div className="vir-interview-context">
          <span className="vir-eyebrow">Interview context</span>
          <strong>{sessionTitle}</strong>
          <span>{candidateName}</span>
          <span>{session?.round || "Interview round"}</span>
          <span>
            {session?.timeZone || session?.timezone || "Asia/Kolkata"} •{" "}
            {session?.provider || "DAILY"}
          </span>
        </div>
      </aside>
    </main>
  );

  const renderScorecard = () => (
    <main className="vir-scorecard-screen">
      <section className="vir-scorecard-intro">
        <div className="vir-state-icon">
          <ClipboardCheck size={34} />
        </div>
        <span className="vir-eyebrow">Panel evaluation</span>
        <h1>Complete the interview scorecard</h1>
        <p>
          Your structured feedback will automatically update the candidate workflow and
          approval queue.
        </p>
        <div className="vir-candidate-summary">
          <div>{(candidateName || "C").slice(0, 1).toUpperCase()}</div>
          <span>
            <strong>{candidateName || "Candidate"}</strong>
            <small>{sessionTitle}</small>
          </span>
        </div>
        <div className="vir-ai-note">
          <Sparkles size={18} />
          <span>
            <strong>Automation ready</strong>
            Selection moves the candidate to the next configured stage. Rejection
            follows the approved communication and access policy.
          </span>
        </div>
      </section>

      <form className="vir-scorecard-form" onSubmit={submitScorecard}>
        <div className="vir-form-title">
          <div>
            <span className="vir-eyebrow">Required assessment</span>
            <h2>Ratings & recommendation</h2>
          </div>
          <span>1 = Low • 5 = Exceptional</span>
        </div>
        <RatingField
          label="Technical / functional skill"
          value={scorecard.technical}
          onChange={(technical) => setScorecard((value) => ({ ...value, technical }))}
        />
        <RatingField
          label="Communication"
          value={scorecard.communication}
          onChange={(communication) =>
            setScorecard((value) => ({ ...value, communication }))
          }
        />
        <RatingField
          label="Role fit"
          value={scorecard.roleFit}
          onChange={(roleFit) => setScorecard((value) => ({ ...value, roleFit }))}
        />
        <RatingField
          label="Culture & values"
          value={scorecard.cultureFit}
          onChange={(cultureFit) =>
            setScorecard((value) => ({ ...value, cultureFit }))
          }
        />
        <RatingField
          label="Overall rating *"
          value={scorecard.overall}
          onChange={(overall) => setScorecard((value) => ({ ...value, overall }))}
        />

        <label className="vir-field">
          <span>Final recommendation *</span>
          <select
            value={scorecard.recommendation}
            onChange={(event) =>
              setScorecard((value) => ({
                ...value,
                recommendation: event.target.value,
              }))
            }
          >
            <option value="">Choose decision</option>
            {RECOMMENDATIONS.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </label>

        <div className="vir-text-grid">
          <label className="vir-field">
            <span>Key strengths</span>
            <textarea
              rows={3}
              value={scorecard.strengths}
              onChange={(event) =>
                setScorecard((value) => ({ ...value, strengths: event.target.value }))
              }
              placeholder="What stood out positively?"
            />
          </label>
          <label className="vir-field">
            <span>Concerns / risks</span>
            <textarea
              rows={3}
              value={scorecard.concerns}
              onChange={(event) =>
                setScorecard((value) => ({ ...value, concerns: event.target.value }))
              }
              placeholder="Any gaps requiring validation?"
            />
          </label>
        </div>
        <label className="vir-field">
          <span>Panel notes</span>
          <textarea
            rows={4}
            value={scorecard.notes}
            onChange={(event) =>
              setScorecard((value) => ({ ...value, notes: event.target.value }))
            }
            placeholder="Add evidence-based interview observations…"
          />
        </label>
        <button
          type="submit"
          className="vir-primary-button"
          disabled={scoreSubmitting}
        >
          {scoreSubmitting ? (
            <Loader2 size={18} className="vir-spin" />
          ) : (
            <Send size={18} />
          )}
          {scoreSubmitting ? "Submitting evaluation…" : "Submit scorecard & continue workflow"}
        </button>
      </form>
    </main>
  );

  const renderComplete = () => (
    <main className="vir-state-screen">
      <div className="vir-state-icon is-success">
        <Check size={36} />
      </div>
      <span className="vir-eyebrow">
        {scoreSubmitted ? "Evaluation recorded" : "Interview completed"}
      </span>
      <h1>{isStaff ? "Interview workflow updated" : "Thank you for your time"}</h1>
      <p>
        {isStaff
          ? "Your scorecard has been secured and routed to the configured hiring approval stage."
          : "The hiring team has been notified. You can track the next update from your candidate account."}
      </p>
      <div className="vir-security-line">
        <ShieldCheck size={16} /> Secure room closed • participant access expired
      </div>
      {onClose && (
        <button type="button" className="vir-primary-button vir-finish-button" onClick={onClose}>
          <ArrowLeft size={17} /> Return to dashboard
        </button>
      )}
    </main>
  );

  const renderErrorScreen = (expired = false) => (
    <main className="vir-state-screen">
      <div className="vir-state-icon is-error">
        <AlertTriangle size={34} />
      </div>
      <span className="vir-eyebrow">{expired ? "Link expired" : "Unable to connect"}</span>
      <h1>
        {expired
          ? "This interview room is now closed"
          : "Secure interview could not be opened"}
      </h1>
      <p>
        {expired
          ? "For security, the link is valid only for its scheduled one-hour interview window."
          : error}
      </p>
      {!expired && (
        <button type="button" className="vir-primary-button" onClick={loadSecureSession}>
          <RefreshCw size={17} /> Try again
        </button>
      )}
      {onClose && (
        <button type="button" className="vir-secondary-button" onClick={onClose}>
          <ArrowLeft size={17} /> Return
        </button>
      )}
    </main>
  );

  return (
    <div className={`vir-root vir-phase-${phase}`} ref={rootRef}>
      {renderHeader()}
      {phase !== "error" && renderError()}
      {phase === "loading" && renderLoading()}
      {phase === "waiting" && renderWaiting()}
      {phase === "prejoin" && renderPrejoin()}
      {(phase === "joining" || phase === "joined" || phase === "leaving") &&
        renderLiveMeeting()}
      {phase === "scorecard" && renderScorecard()}
      {phase === "complete" && renderComplete()}
      {phase === "expired" && renderErrorScreen(true)}
      {phase === "error" && renderErrorScreen(false)}
    </div>
  );
}
